import { useMemo } from "react"
import { buildSnapshot, sparklinePoints } from "../performanceSnapshot"

/*
    Performance Snapshot (MPN-1530 §2).

    Sits above the tabs and stays put whichever tab is open, because the six numbers it
    carries are the ones you want while reading any of them: noticing CPM has doubled
    matters just as much on the Audience tab as on Conversions.

    Sparklines are inline SVG polylines, not chart instances. The dashboard already turns
    Apex animations off because mounting many charts at once is what made this page
    stutter (see DefaultApexOptions), and six always-mounted charts above every tab would
    be that problem again. A polyline is a string.

    All six read from data the page already fetched, so changing the Date Filter moves
    them with everything else and costs no extra request, which is what §2 asks for.
*/

export default function PerformanceSnapshot({ data }: { data: any }) {
  const metrics = useMemo(() => buildSnapshot(data), [data])

  return (
    <div className="mon-snapshot">
      <div className="mon-snapshot__head">
        <span className="mon-snapshot__title">Performance Snapshot</span>
        <span className="mon-snapshot__sub">for the selected date range</span>
      </div>
      <div className="mon-snapshot__grid">
        {metrics.map((metric) => (
          <div className="mon-snapshot__metric" key={metric.key}>
            <div className="mon-snapshot__label">{metric.label}</div>
            <div className="mon-snapshot__value">{metric.value}</div>
            <Sparkline values={metric.series} label={metric.label} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Sparkline({ values, label }: { values: number[]; label: string }) {
  const points = sparklinePoints(values)
  if (!points) {
    // No line rather than an empty box with axes: nothing happened in this window, and
    // a flat line at zero would imply we measured zero rather than measured nothing.
    return <div className="mon-snapshot__spark mon-snapshot__spark--empty" aria-hidden="true" />
  }
  return (
    <svg
      className="mon-snapshot__spark"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${label} trend over the selected date range`}
    >
      <polyline points={points} />
    </svg>
  )
}
